import store from 'redux/store';
import { deleteEvent, raiseFileEvent, raiseProcessEvent } from 'redux/slices/eventsSlice';
import { isFileEventType } from 'utils/eventTypes';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditEvent } from './edit/showEditEvent';

type EventEntity = Extract<ActionEntity, { type: 'event' }>;

const isAllEvent = (entities: readonly ActionEntity[]): entities is readonly EventEntity[] => entities.every((entity) => entity.type === 'event');

/**
 * Action definitions for events. `createEvent` / `addToTransportEvent` are
 * scoped; `edit` / `raiseEvent` / `showLive` / `showHistory` / `delete` are
 * contextual and single. `raiseEvent` is hidden unless the one event is a file
 * or process event (dispatching the matching raise thunk).
 *
 * The two navigations replace the old single `showInstances`, along the same
 * line the two pages are split on -- whether an occurrence has been consumed.
 * Neither is type-gated: both pages are kind-blind, so the file/process
 * branching `showInstances` needed is gone.
 */
export const eventActionDefinitions: ActionDefinitions = [
  {
    id: 'createEvent',
    scope: 'type',
    label: 'New Event',
    resolve: (ctx) => () => void showEditEvent({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditEvent({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'raiseEvent',
    label: 'Raise Event',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'hidden';
      const [only] = entities;
      if (isFileEventType(only.eventType)) {
        return () => void store.dispatch(raiseFileEvent({ event_id: only.id })).then(() => refresh());
      }
      if (only.eventType === 'processEvent') {
        return () => void store.dispatch(raiseProcessEvent({ event_id: only.id })).then(() => refresh());
      }
      return 'hidden';
    },
  },
  {
    id: 'showLive',
    label: 'Show live',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      // `focus` rather than a filter: the card is force-shown whether or not
      // the event is out of balance, pinned to the top, without disturbing
      // whatever filters the operator already had. A balanced event still gets
      // a card reading surplus 0 / demand 0, which answers "why is nothing
      // happening?" where an empty page would not.
      return () => navigate(`/automation/live_events?focus=${only.id}`);
    },
  },
  {
    id: 'showHistory',
    label: 'Show history',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => navigate(`/automation/event_history?event_id=${only.id}`);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllEvent(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      const single = entities.length === 1 ? entities[0] : null;
      return deleteExecutor(entities, (entity) => store.dispatch(deleteEvent({ zone_name: entity.zoneName, name: entity.name })), {
        refresh,
        confirmTitle: 'Delete event',
        confirmBody: single ? `Delete event "${single.name}" in zone "${single.zoneName}"?` : `Delete ${entities.length} events?`,
      });
    },
  },
  {
    id: 'addToTransportEvent',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllEvent(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'event',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName || '-'} | ${single.name}` : `${entities.length} events` }
      );
    },
  },
];
